/**
 * Livechat Analytics Dashboard
 *
 * Main dashboard for user journey analytics with tabs for:
 * - Overview: Key metrics and stat cards
 * - Sources: Traffic source attribution table
 * - User Flow: Sankey diagram of page navigation
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart3, Globe, GitBranch } from 'lucide-react'
import dayjs from 'dayjs'
import { Tabs } from 'vnetwork-ui'
import SubHeaders from '@/components/Headers/SubHeaders'
import DateRangeSelector, { DateRange } from '@/components/DateRangeSelector'
import useTabs from '@/hooks/useTabs'

import OverviewSection from './OverviewSection'
import SourcesSection from './SourcesSection'
import FlowSection from './FlowSection'

const Analytics: React.FC = () => {
    const { t } = useTranslation()
    const [dateRange, setDateRange] = useState<DateRange>({
        gte: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        lte: dayjs().format('YYYY-MM-DD'),
    })

    const { TabProps, TabComponent } = useTabs({
        tabs: [
            {
                label: t('Overview'),
                value: 'overview',
                startIcon: <BarChart3 className="h-4 w-4" />,
                component: (
                    <OverviewSection from={dateRange.gte} to={dateRange.lte} />
                ),
            },
            {
                label: t('Sources'),
                value: 'sources',
                startIcon: <Globe className="h-4 w-4" />,
                component: (
                    <SourcesSection from={dateRange.gte} to={dateRange.lte} />
                ),
            },
            {
                label: t('User Flow'),
                value: 'flow',
                startIcon: <GitBranch className="h-4 w-4" />,
                component: (
                    <FlowSection from={dateRange.gte} to={dateRange.lte} />
                ),
            },
        ],
    })

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { path: '/livechat', title: t('Livechat') },
                    { path: '/livechat/analytics', title: t('Analytics') },
                ]}
            />

            {/* Header with tabs and date selector */}
            <div className="lg:flex lg:flex-wrap items-center gap-4 mb-2 bg-white rounded-md justify-between">
                <div className="flex items-center gap-2">
                    <Tabs {...TabProps} tabContainerClassName="pt-0" />
                </div>
                <div className="p-2">
                    <DateRangeSelector
                        value={dateRange}
                        onChange={setDateRange}
                        placement="right"
                    />
                </div>
            </div>

            {/* Tab content */}
            <div className="mt-2">{TabComponent}</div>
        </div>
    )
}

export default Analytics
